import { useMemo, useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { dbui_v1_TransformRequest } from '@antimatterhq/asl-api-ts-v1';
import { useFormikContext } from 'formik';
import { useConfigDataSource } from '@/contexts/ConfigDataSourceContext';
import { DataSourcesDataPreviewDialog } from '@/components/dataSources/dataPreviewDialog/DataSourcesDataPreviewDialog';
import { GoldDataSourceSchemaValues } from '@/zod/schemas/dataSource/Gold';
import {
  setGoldFormValues,
  mapSilverTableToTransformRequest,
  mapGoldTableToTransformRequest,
} from '@/lib/datasource/';
import { useGetTablePreview } from '@/hooks/dbui/useGetTablePreview';
import { useGetAndPollTransform } from '@/hooks/components/useGetAndPollTransform';
import { useGetFullyQualifiedTableName } from '@/hooks/components/useGetFullyQualifiedTableName';

export interface GoldDataPreviewContainerProps {
  tableIndex: number;
  isModifyTable: boolean;
  disabled?: boolean;
}

export const GoldDataPreviewContainer = ({
  tableIndex,
  isModifyTable,
  disabled,
}: GoldDataPreviewContainerProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const handleDataPreviewModalClose = () => setIsDialogOpen(false);

  const { dataSource: initialDatasource } = useConfigDataSource();

  const { name: bronzeTableName = '' } = useGetFullyQualifiedTableName({
    tableName: initialDatasource.spec.bronze?.bronzeTable || '',
    stage: 'bronze',
  });

  // get the current pre-transform values
  const { values } = useFormikContext<GoldDataSourceSchemaValues>();

  // set the autoloader input
  const autoloaderInput = useMemo(() => {
    return {
      location: initialDatasource.spec.autoloader?.location || '',
      ...initialDatasource.spec.autoloader,
    };
  }, [initialDatasource]);

  // if we are not using autoloader, call the preview table for the "source table"
  const { data: bronzeTablePreview } = useGetTablePreview({
    name: bronzeTableName,
    disabled: !!autoloaderInput.location,
  });

  // handle the transform operation
  const transformRequest = useMemo(() => {
    // preset recreating this request object with every form change
    if (!isDialogOpen) {
      return {
        autoloaderInput: undefined,
        input: undefined,
        transforms: [],
        usePreset: initialDatasource.spec.usePreset,
      };
    }

    const datasource = setGoldFormValues(initialDatasource, values);
    const { autoloader, gold, silver } = datasource.spec;
    const goldTable = isModifyTable
      ? gold?.presetOverrides?.modifyTables?.[tableIndex]
      : gold?.presetOverrides?.addTables?.[tableIndex];

    if (!goldTable) {
      throw new Error('Table missing for gold data preview');
    }

    const silverTable = isModifyTable
      ? silver?.transform?.presetOverrides?.modifyTables?.find(
          (q) => q.name === goldTable.sourceTable,
        )
      : silver?.transform?.presetOverrides?.modifyTables?.find(
          (q) => q.name === goldTable.sourceTable,
        );

    if (!silverTable) {
      throw new Error('Silver table missing for gold data preview');
    }

    const transforms = [
      mapSilverTableToTransformRequest(silverTable, isModifyTable),
      mapGoldTableToTransformRequest(goldTable, isModifyTable),
    ];

    const request: Omit<dbui_v1_TransformRequest, 'warehouse'> = {
      autoloaderInput: autoloader?.location ? autoloader : undefined,
      input: bronzeTablePreview ? bronzeTablePreview : undefined,
      transforms,
      usePreset: initialDatasource.spec.usePreset,
    };

    return request;
  }, [
    bronzeTablePreview,
    initialDatasource,
    values,
    isModifyTable,
    tableIndex,
    isDialogOpen,
  ]);

  const {
    stages,
    isLoading: previewTableIsLoading,
    error: previewTableResponseError,
  } = useGetAndPollTransform(transformRequest, isDialogOpen);

  const previewTable = useMemo(
    () => stages?.find((stage) => stage.transformType === 'Gold'),
    [stages],
  );

  const sourceTable = useMemo(
    () => stages?.find((stage) => stage.transformType === 'SilverTransform'),
    [stages],
  );

  return (
    <>
      <Button
        onClick={() => setIsDialogOpen(true)}
        variant="primary"
        disabled={disabled}
        leftIcon="radix-icons:external-link"
      >
        Open preview
      </Button>
      <DataSourcesDataPreviewDialog
        sourceTable={sourceTable}
        previewTable={previewTable}
        sourceTableStage={'SilverTransform'}
        previewTableStage="Gold"
        isOpen={isDialogOpen}
        onClose={handleDataPreviewModalClose}
        sourceTableIsLoading={previewTableIsLoading}
        previewTableIsLoading={previewTableIsLoading}
        sourceTableResponseError={previewTableResponseError}
        previewTableResponseError={previewTableResponseError}
      />
    </>
  );
};
